import { Icon, MessageBar, MessageBarType, Stack, Text, mergeStyles, getTheme, Spinner } from '@fluentui/react';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { lookupCase, QueueConfiguration, getCaseFeedback, Case, Feedback, caseSearch } from 'services';
import { LookupContext, lookupReducer } from './LookupContext';
import { AxiosError } from 'axios';
import { LookupComponent } from './LookupComponent';
import { SpinnerSize, Link, IDropdownOption } from '@fluentui/react';
import { TableComponent, StyledTag } from 'components/shared';
import { getUniqueLabels } from 'components/util';
import { LookupSearch } from './LookupSearch';
import { isDarkTheme } from 'theme';

const searchContainerWidth = mergeStyles({ width: 500 });
const searchViewContainer = mergeStyles('h-50', 'mx-auto', searchContainerWidth);
const searchResultViewContainer = mergeStyles('pl-5', 'pt-4', searchContainerWidth);

const logoClass = mergeStyles({
  fontSize: 50,
  height: 50,
  width: 50,
  margin: '0 25px'
});

export interface LookupComponentProps {
  configs: QueueConfiguration[];
}

export const Lookup: React.FunctionComponent<LookupComponentProps> = props => {
  const { palette } = getTheme();
  const container = { backgroundColor: palette.neutralLighterAlt };
  const separatorClass = mergeStyles({ height: 1, borderTop: '1px solid rgb(201, 203, 207)' }, 'w-100');
  const resultTableClass = mergeStyles({ backgroundColor: palette.white }, 'rounded shadow-1 overflow-auto');
  const primaryIconClass = mergeStyles(
    {
      color: palette.themePrimary
    },
    logoClass
  );
  const infoTextColor = mergeStyles({
    color: palette.neutralDark
  });
  const [state, dispatch] = useReducer(lookupReducer, {
    search: undefined,
    result: undefined,
    curCase: undefined,
    curFeedbacks: undefined
  });
  const [apiError, setApiError] = useState<string | undefined>(undefined);
  const [isAccessError, setIsAccessError] = useState<boolean>(false);
  const [isRootPageLoading, setIsRootPageLoading] = useState<boolean>(true);
  const [searchParamError, setSearchParamError] = useState<string | undefined>(undefined);
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [currentTenant, setCurrentTenant] = useState<string | undefined>(undefined);
  const handleSearch = useCallback(
    (value: string) => {
      value = value.trim();
      if (value !== '') {
        dispatch({ type: 'SEARCH', search: value, result: undefined });
        setApiError(undefined);
        currentTenant &&
          caseSearch(currentTenant, value)
            .then(result => dispatch({ type: 'SEARCH', search: value, result: { term: value, data: result } }))
            .catch((error: AxiosError) => {
              setApiError(error.message);
            });
      }
    },
    [dispatch, currentTenant]
  );
  const getViewFromState = useCallback((): JSX.Element => {
    // Single case overturn view.
    if (state.curConfig && state.curCase && state.curFeedbacks) {
      return <LookupComponent case={state.curCase} config={state.curConfig} feedbacks={state.curFeedbacks} />;
    }

    const tenantOptions = props.configs.reduce((prev, cur) => {
      if (prev.every(item => item.key !== cur.tenant.tenantId)) {
        prev.push({ key: cur.tenant.tenantId, text: cur.tenant.displayName });
      }
      return prev;
    }, [] as IDropdownOption[]);

    // Search View with results.
    if (state.search !== undefined) {
      return (
        <Stack className="w-100 h-100">
          <Stack.Item className="pt-3 pb-4 pl-5">
            <Text aria-level={1} variant="superLarge">
              Case Lookup
            </Text>
          </Stack.Item>
          <Stack.Item>
            <div className={separatorClass} />
          </Stack.Item>
          <Stack.Item className={searchResultViewContainer}>
            <LookupSearch
              className={searchContainerWidth}
              defaultKey={currentTenant || ''}
              options={tenantOptions}
              onSearch={handleSearch}
              onTenantChange={key => {
                history.replace({
                  search: `?tenantid=${key}`
                });
              }}
            />
          </Stack.Item>
          <Stack.Item grow className="my-4 mx-5 overflow-auto">
            <div className={resultTableClass + ' ms-depth-4'}>
              {!state.result && <Spinner size={SpinnerSize.large} className="p-5" />}
              {state.result && (
                <TableComponent
                  key="userReportTable"
                  clickable={false}
                  data={state.result.data
                    .sort(
                      (a, b) => new Date(b.caseCreationDateTime).getTime() - new Date(a.caseCreationDateTime).getTime()
                    )
                    .map(item => {
                      const linkTarget = `#/lookup?tenantid=${item.tenantId}&titleid=${item.titleId}&subjecttype=${
                        item.subjectType
                      }&subjectid=${encodeURIComponent(item.subjectId)}&caseid=${item.caseId}`;
                      const tags = getUniqueLabels(item.opinionCategoryLabels).map(value => {
                        return <StyledTag className="mr-1 mb-1" key={value} tag={value} />;
                      });
                      return {
                        Queue: item.subjectType,
                        'Reported subject': item.subjectLabels?.join(', '),
                        'Applied trait': tags,
                        'Action taken': item.actions.map(
                          (action): JSX.Element => {
                            return (
                              <Text key={action.action}>
                                {action.action}
                                <br />
                              </Text>
                            );
                          }
                        ),
                        Strikes: item.casePointsValue ? `${item.casePointsValue}` : '',
                        'Account Total': item.activePointsAtTimeOfAction ? `${item.activePointsAtTimeOfAction}` : '',
                        'Case status': item.status,
                        'Case created': item.caseCreationDateTime,
                        'Case closed': item.status !== 'Open' ? item.caseClosedDateTime : '',
                        '': (
                          <Link
                            styles={{ root: { color: isDarkTheme() ? palette.themeTertiary : palette.themePrimary } }}
                            href={linkTarget}
                          >
                            View Case
                          </Link>
                        )
                      };
                    })}
                />
              )}
            </div>
          </Stack.Item>
        </Stack>
      );
    }

    // Search view,
    return (
      <Stack className={searchViewContainer} horizontalAlign="center" verticalAlign="center">
        <Stack.Item>
          <Icon iconName="SearchAndApps" className={primaryIconClass} />
        </Stack.Item>
        <Stack.Item className="pb-4 pt-1">
          <Text aria-level={1} role="heading" variant="superLarge">
            Case Lookup
          </Text>
        </Stack.Item>
        <Stack.Item className="w-100">
          <LookupSearch
            defaultKey={currentTenant || ''}
            options={tenantOptions}
            onSearch={handleSearch}
            onTenantChange={key => {
              history.replace({
                search: `?tenantid=${key}`
              });
            }}
          />
        </Stack.Item>
        <Stack.Item className="my-3">
          <Text variant="medium" className={infoTextColor}>
            Enter exact value to look up cases.
          </Text>
        </Stack.Item>
      </Stack>
    );
  }, [
    state,
    handleSearch,
    infoTextColor,
    primaryIconClass,
    resultTableClass,
    separatorClass,
    currentTenant,
    props.configs,
    history
  ]);

  useEffect(() => {
    dispatch({ type: 'CLEAR' });
    const params = new URLSearchParams(location.search);
    const paramCount = Array.from(params).length;
    const tenantId = params.get('tenantid');
    const titleId = params.get('titleid');
    const subjectType = params.get('subjecttype');
    const subjectId = params.get('subjectid');
    const caseId = params.get('caseid');

    if (tenantId === null) {
      setSearchParamError('TenantID');
      return;
    }

    setCurrentTenant(tenantId);

    if (paramCount === 1) {
      // If tenant id is only parameter, we're searching for cases.
      setSearchParamError(undefined);
      setApiError(undefined);
      setIsAccessError(!props.configs.some(config => config.tenant.tenantId === tenantId));
      setIsRootPageLoading(false);
      return;
    }

    if (titleId === null) {
      setSearchParamError('TitleID');
      return;
    }

    if (subjectType === null) {
      setSearchParamError('SubjectType');
      return;
    }

    if (subjectId === null) {
      setSearchParamError('SubjectID');
      return;
    }

    if (caseId === null) {
      setSearchParamError('CaseID');
      return;
    }

    const curConfig = props.configs.find(
      config =>
        config.tenant.tenantId === tenantId && config.title.titleId === titleId && config.subjectType === subjectType
    );
    setIsAccessError(!curConfig);
    if (!curConfig) {
      return;
    }

    setIsRootPageLoading(true);
    Promise.all([
      lookupCase(tenantId, titleId, subjectType, subjectId, caseId),
      getCaseFeedback(tenantId, titleId, subjectType, subjectId, caseId)
    ])
      .then((result: [Case, Feedback[]]) => {
        dispatch({ type: 'SELECT', curCase: result[0], curFeedbacks: result[1], curConfig: curConfig });
        setSearchParamError(undefined);
        setApiError(undefined);
        setIsRootPageLoading(false);
      })
      .catch((error: AxiosError) => {
        let errorMessage = error.message;
        if (error.code === '404') {
          errorMessage = 'A case was not found for the specified parameters.';
        }

        if (error.code === '401') {
          errorMessage = 'Not authorized to view cases for specified queue.';
        }

        setIsRootPageLoading(false);
        setApiError(errorMessage);
      });
  }, [props, props.configs, dispatch, location]);

  if (searchParamError) {
    return (
      <MessageBar messageBarType={MessageBarType.error} isMultiline={true} className="mx-auto my-4 w-50">
        <div>
          {searchParamError} value was not provided. Ensure it is specified as{' '}
          <b>{searchParamError.toLocaleLowerCase()}</b> (case sensitive).
        </div>
      </MessageBar>
    );
  }

  if (isAccessError) {
    return (
      <MessageBar messageBarType={MessageBarType.error} isMultiline={true} className="mx-auto my-4 w-50">
        <div>
          Invalid queue provided, or you do not have access to view cases in this queue.
          <br />
          <br />
          Tenant ID: {params.get('tenantid')} <br />
          Title ID: {params.get('titleid')} <br />
          Subject Type: {params.get('subjecttype')}
        </div>
      </MessageBar>
    );
  }

  if (apiError) {
    return (
      <MessageBar messageBarType={MessageBarType.error} isMultiline={true} className="mx-auto my-4 w-50">
        <div>{apiError}</div>
      </MessageBar>
    );
  }

  if (isRootPageLoading) {
    return (
      <Stack className="h-100 w-100" verticalAlign="center" horizontalAlign="center">
        <Spinner size={SpinnerSize.large} />
      </Stack>
    );
  }

  return (
    <LookupContext.Provider value={{ state, dispatch }}>
      <div className="h-100 w-100" style={container}>
        {getViewFromState()}
      </div>
    </LookupContext.Provider>
  );
};
